import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { AdminBadge } from './AdminBadge';
import { 
  Search, User, Gamepad2, Star, Calculator, X, Loader2,
  Crown, BadgeCheck, Shield, TrendingUp
} from 'lucide-react';
import { getAvatarUrl } from '../utils/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GlobalSearchBar = ({ compact = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Quick navigation items
  const quickLinks = [
    { type: 'page', label: 'Tarot Readings', path: '/tarot', icon: Star },
    { type: 'page', label: 'Cardology Calculator', path: '/calculations', icon: Calculator },
    { type: 'page', label: 'Cosmic Quest', path: '/games/rpg', icon: Gamepad2 },
  ];

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      const totalResults = results.length + quickLinks.length;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalResults);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + totalResults) % totalResults);
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        if (selectedIndex < results.length) {
          navigate(`/user/${results[selectedIndex].id}`);
        } else {
          navigate(quickLinks[selectedIndex - results.length].path);
        }
        setIsOpen(false);
        setQuery('');
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, results, navigate]);

  // Search users
  const searchUsers = async (searchQuery) => {
    if (!token || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/search`, {
        params: { q: searchQuery, limit: 5 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data || []);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => searchUsers(query), 300);
    } else {
      setResults([]);
    }
    
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, token]);

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleSelect = (path) => {
    navigate(path);
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(-1);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search Input */}
      <div className={`relative flex items-center ${compact ? 'w-40 lg:w-56' : 'w-64'}`}>
        <Search className="absolute left-3 w-4 h-4 text-babylon-text-dim pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          placeholder="Search users..."
          className="w-full pl-9 pr-8 py-1.5 bg-babylon-void/80 border border-babylon-gold/20 rounded-lg 
                     text-sm text-babylon-text placeholder:text-babylon-text-dim/60
                     focus:outline-none focus:border-babylon-gold/50 focus:ring-1 focus:ring-babylon-gold/30
                     transition-all font-medium tracking-wide"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); }}
            className="absolute right-2 p-0.5 text-babylon-text-dim hover:text-babylon-gold transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 bg-babylon-obsidian border border-babylon-gold/30 
                       rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50 min-w-[280px]"
          >
            {/* Loading State */}
            {loading && (
              <div className="px-4 py-3 flex items-center gap-2 text-babylon-text-dim">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Searching...</span>
              </div>
            )}

            {/* User Results */}
            {!loading && results.length > 0 && (
              <div className="p-2">
                <p className="px-2 py-1 text-[10px] uppercase tracking-widest text-babylon-text-dim font-bold">
                  Users
                </p>
                {results.map((user, idx) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelect(`/user/${user.id}`)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      selectedIndex === idx 
                        ? 'bg-babylon-gold/20 text-babylon-gold' 
                        : 'hover:bg-babylon-gold/10 text-babylon-text'
                    }`}
                  >
                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                      user.is_admin ? 'border-yellow-500 bg-yellow-500/20' : 'border-babylon-gold/30 bg-babylon-gold/10'
                    }`}>
                      {user.avatar_url ? (
                        <img 
                          src={getAvatarUrl(user.avatar_url)} 
                          alt="" 
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-bold text-babylon-gold">
                          {(user.name || user.username || 'U').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm truncate">
                          {user.name || user.username}
                        </span>
                        {user.email_verified && (
                          <BadgeCheck size={12} className="text-blue-400 flex-shrink-0" />
                        )}
                        {user.is_admin && (
                          <AdminBadge size="small" showText={false} animated={false} />
                        )}
                      </div>
                      {user.username && user.name && (
                        <p className="text-xs text-babylon-text-dim truncate">@{user.username}</p>
                      )}
                    </div>

                    {/* Level badge if has RPG character */}
                    {user.level && (
                      <span className="text-[10px] px-2 py-0.5 bg-babylon-gold/20 text-babylon-gold rounded-full font-bold">
                        Lv.{user.level}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* No Results */}
            {!loading && query.length >= 2 && results.length === 0 && (
              <div className="px-4 py-3 text-sm text-babylon-text-dim text-center">
                No users found for "<span className="text-babylon-gold font-medium">{query}</span>"
              </div>
            )}

            {/* Quick Links */}
            <div className="p-2 border-t border-babylon-gold/10">
              <p className="px-2 py-1 text-[10px] uppercase tracking-widest text-babylon-text-dim font-bold">
                Quick Navigation
              </p>
              {quickLinks.map((item, idx) => {
                const itemIndex = results.length + idx;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleSelect(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                      selectedIndex === itemIndex 
                        ? 'bg-babylon-gold/20 text-babylon-gold' 
                        : 'hover:bg-babylon-gold/10 text-babylon-text-dim hover:text-babylon-text'
                    }`}
                  >
                    <item.icon size={16} className="flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Search hint */}
            <div className="px-4 py-2 bg-babylon-void/50 border-t border-babylon-gold/10">
              <p className="text-[10px] text-babylon-text-dim font-medium tracking-wide">
                <kbd className="px-1.5 py-0.5 bg-babylon-obsidian rounded border border-babylon-gold/20 text-babylon-gold mr-1">↑↓</kbd>
                Navigate
                <kbd className="px-1.5 py-0.5 bg-babylon-obsidian rounded border border-babylon-gold/20 text-babylon-gold mx-1 ml-3">Enter</kbd>
                Select
                <kbd className="px-1.5 py-0.5 bg-babylon-obsidian rounded border border-babylon-gold/20 text-babylon-gold mx-1 ml-3">Esc</kbd>
                Close
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalSearchBar;
